import type passport from 'passport'
import type { NextApiRequest, NextApiResponse } from 'next'
import type { Platforms, ClientAccountsResp } from 'shared/api/accounts'
import { THIRD_PARTY_PLATFORM, THIRD_PARTY_PROVIDER, SUPERMETRICS_PLATFORM } from 'shared/thirdParty'
import { type FivetranStrategy } from 'src/dataSource/thirdParty/fivetran'
import { type SupermetricsStrategy } from '@precis-digital/passport-supermetrics'
import type { IPlatform, IThirdPartyPlatform, IThirdPartyProvider } from 'src/dataSource/constants'

export interface ClientAccountsWithMetadata extends ClientAccountsResp {
  connectionStatus: 'connected' | 'disconnected' | 'disabled'
  recommendationEngine: 'enabled' | 'paused' | 'not_supported'
}

/** Authentication route taken when connecting a platform that offers both. */
export type ConnectAuthenticationChoice = 'user_account' | 'service_account'

/** Which step of the connect flow to open, and for what. */
export type ConnectTarget =
  | {
      kind: 'platform'
      platform: IPlatform
      /**
       * `service_account` opens the service account access dialog,
       * `user_account` pre-selects the platform and goes to its sign-in step.
       */
      authentication: ConnectAuthenticationChoice
    }
  | {
      kind: 'third_party_provider'
      provider: IThirdPartyProvider
      /** Set when a specific platform behind the provider was asked for. */
      platform?: IThirdPartyPlatform
    }

export interface InternalProfile {
  externalUserId: string
  externalUserName: string
  externalUserEmail?: string
}

export interface Profile {
  id: string
  displayName: string
}

export interface AmazonProfile extends Profile {
  _json?: { email: string }
}

export interface GoogleProfile extends Profile {
  _json?: { email: string }
}

export interface SnapchatProfile extends Profile {
  _json?: {
    me: {
      id: string
      display_name: string
    }
  }
}

export interface TiktokProfile extends Profile {
  _json?: {
    data: {
      core_user_id: string
      display_name: string
    }
  }
}

/** https://developers.pinterest.com/docs/api/v5/user_account-get */
export interface PinterestProfile extends Profile {
  _json?: {
    id: string
    username: string
    business_name: string | null
    account_type: 'PINNER' | 'BUSINESS'
    profile_image: string
  }
}

export const SupportedPlatformsEnum = {
  AMAZON: 'amazon',
  CRITEO: 'criteo',
  FACEBOOK: 'facebook',
  GOOGLE: 'google',
  LINKEDIN: 'linkedin',
  MICROSOFT: 'microsoft_advertising',
  SNAPCHAT: 'snapchat',
  TIKTOK: 'tiktok',
  PINTEREST: 'pinterest'
} as const

export const SUPPORTED_PLATFORMS = [
  SupportedPlatformsEnum.AMAZON,
  SupportedPlatformsEnum.CRITEO,
  SupportedPlatformsEnum.FACEBOOK,
  THIRD_PARTY_PROVIDER.FIVETRAN,
  SupportedPlatformsEnum.GOOGLE,
  SupportedPlatformsEnum.LINKEDIN,
  SupportedPlatformsEnum.MICROSOFT,
  SupportedPlatformsEnum.SNAPCHAT,
  SupportedPlatformsEnum.TIKTOK,
  SupportedPlatformsEnum.PINTEREST,
  ...Object.values(SUPERMETRICS_PLATFORM)
] as const

export type SupportedPlatforms = (typeof SUPPORTED_PLATFORMS)[number]

export interface OauthConfig {
  clientID: string
  clientSecret: string
  developerToken?: string
  callbackURL: string
  authorizationURL?: string
  profileURL?: string
  tokenURL?: string
  scopes?: string[]
  graphAPIVersion?: string
  getStrategy: GetStrategy
  mappedProfileFields: <T extends Profile>(profile: T) => InternalProfile
  params?: {
    accessType?: string
    prompt?: string
  }
}

interface GetStrategyOptions {
  type?: 'login' | 'callback'
  alvieClientId?: number
  dataSourcePlatform?: Platforms
  credentialId?: string
  reconnectAccountId?: string
}
export interface GetThirdPartyStrategyOptions {
  type: 'prepare' | 'reconnect' | 'cleanup' | 'callback'
  alvieClientId: number
  dataSourcePlatform: Platforms
}

type GetThirdPartyStrategy<TStrategy> = (
  authToken: string,
  options: GetThirdPartyStrategyOptions,
  internalAuthToken?: string
) => Promise<TStrategy> | TStrategy

export type ThirdStrategy = FivetranStrategy | SupermetricsStrategy
export interface ThirdPartyOauthConfig<TStrategy = ThirdStrategy> {
  clientID?: string
  clientSecret?: string
  callbackURL: string
  scopes?: string[]
  getThirdPartyStrategy: GetThirdPartyStrategy<TStrategy>
  mappedProfileFields: <T extends Profile>(profile: T) => InternalProfile
  params?: {
    accessType?: string
    prompt?: string
  }
}

export interface ThirdPartyOauthConfigWithClientInfo<TStrategy = ThirdStrategy>
  extends ThirdPartyOauthConfig<TStrategy> {
  clientID: string
  clientSecret: string
}

type GetStrategy = (authToken: string, options?: GetStrategyOptions) => passport.Strategy

export const PLATFORM = {
  ADOBE_ANALYTICS: THIRD_PARTY_PLATFORM.ADOBE_ANALYTICS,
  AMAZON: 'amazon',
  ANALYTICS: 'analytics',
  CRITEO: 'criteo',
  DV360: 'dv360',
  FACEBOOK: 'facebook',
  GA4: 'ga4',
  GCP: 'gcp',
  GOOGLE: 'google',
  GOOGLE_BIGQUERY: 'google_bigquery',
  GOOGLE_SEARCH_CONSOLE: 'google_search_console',
  GOOGLE_SHEETS: 'google_sheets',
  LINKEDIN: 'linkedin',
  MERCHANT_CENTER: 'merchant_center',
  MICROSOFT_ADVERTISING: 'microsoft_advertising',
  PINTEREST_ADS: THIRD_PARTY_PLATFORM.PINTEREST_ADS,
  SHOPIFY: 'shopify',
  SNAPCHAT: 'snapchat',
  TIKTOK: 'tiktok',
  PINTEREST: 'pinterest',
  ADTRACTION: THIRD_PARTY_PLATFORM.ADTRACTION,
  HUBSPOT: THIRD_PARTY_PLATFORM.HUBSPOT,
  KLAVIYO: THIRD_PARTY_PLATFORM.KLAVIYO,
  SPOTIFY_ADS: THIRD_PARTY_PLATFORM.SPOTIFY_ADS,
  REDDIT_ADS: THIRD_PARTY_PLATFORM.REDDIT_ADS,
  RTB_HOUSE: THIRD_PARTY_PLATFORM.RTB_HOUSE,
  WOOCOMMERCE: THIRD_PARTY_PLATFORM.WOOCOMMERCE,
  CJ_AFFILIATE: THIRD_PARTY_PLATFORM.CJ_AFFILIATE,
  TRADEDOUBLER: THIRD_PARTY_PLATFORM.TRADEDOUBLER,
  GOOGLE_CAMPAIGN_MANAGER360: THIRD_PARTY_PLATFORM.GOOGLE_CAMPAIGN_MANAGER360,
  PARTNERIZE: THIRD_PARTY_PLATFORM.PARTNERIZE,
  STACKADAPT: THIRD_PARTY_PLATFORM.STACKADAPT,
  SHOPIFY_SUPERMETRICS: THIRD_PARTY_PLATFORM.SHOPIFY_SUPERMETRICS,
  X_ADS: THIRD_PARTY_PLATFORM.X_ADS,
  STRIPE: THIRD_PARTY_PLATFORM.STRIPE,
  CHATGPT_ADS: 'chatgpt_ads',
  QUANTCAST: THIRD_PARTY_PLATFORM.QUANTCAST
} as const

export interface ThirdPartyStrategy {
  prepare: (req: NextApiRequest, res: NextApiResponse) => Promise<void>
  cleanup?: (req: NextApiRequest, res: NextApiResponse, thirdPartyId: string) => Promise<void>
}

export const infiniteScrollSupportMap: Record<Platforms, boolean> = {
  [PLATFORM.GOOGLE_SHEETS]: false,
  [PLATFORM.AMAZON]: false,
  [PLATFORM.CRITEO]: false,
  [PLATFORM.ANALYTICS]: false,
  [PLATFORM.GA4]: false,
  [PLATFORM.GOOGLE]: false,
  [PLATFORM.DV360]: false,
  [PLATFORM.GOOGLE_SEARCH_CONSOLE]: false,
  [PLATFORM.MERCHANT_CENTER]: false,
  [PLATFORM.GCP]: true,
  [PLATFORM.MICROSOFT_ADVERTISING]: false,
  [PLATFORM.LINKEDIN]: false,
  [PLATFORM.SNAPCHAT]: false,
  [PLATFORM.TIKTOK]: false,
  [PLATFORM.FACEBOOK]: false,
  [PLATFORM.ADOBE_ANALYTICS]: false,
  [PLATFORM.PINTEREST_ADS]: false,
  [PLATFORM.GOOGLE_BIGQUERY]: false,
  [PLATFORM.SHOPIFY]: false,
  [PLATFORM.ADTRACTION]: false,
  [PLATFORM.HUBSPOT]: false,
  [PLATFORM.KLAVIYO]: false,
  [PLATFORM.SPOTIFY_ADS]: false,
  [PLATFORM.REDDIT_ADS]: false,
  [PLATFORM.RTB_HOUSE]: false,
  [PLATFORM.WOOCOMMERCE]: false,
  [PLATFORM.CJ_AFFILIATE]: false,
  [PLATFORM.TRADEDOUBLER]: false,
  [PLATFORM.PINTEREST]: false,
  [PLATFORM.GOOGLE_CAMPAIGN_MANAGER360]: false,
  [PLATFORM.PARTNERIZE]: false,
  [PLATFORM.STACKADAPT]: false,
  [PLATFORM.SHOPIFY_SUPERMETRICS]: false,
  [PLATFORM.X_ADS]: false,
  [PLATFORM.STRIPE]: false,
  [PLATFORM.CHATGPT_ADS]: false,
  [PLATFORM.QUANTCAST]: false
}
